import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import webpayLogo from 'assets/img/webpay.png';
import { createTransaction } from 'lib/api/express/transaction';
import {
  Transaction,
  TransactionProvider,
  TransactionPurpose,
} from 'lib/api/express/transaction/types';
import PageHeader from 'lib/designSystem/PageHeader';
import { showAlert } from 'lib/functions/utils';
import React from 'react';
import { useDispatch } from 'react-redux';
import { saveTransaction } from 'services/redux/transaction';

type Props = {
  open: boolean;
  onClose: () => void;
  amount: number;
  purpose: TransactionPurpose;
  returnUrl: string;
  requestId?: number;
};

export const providers = {
  [TransactionProvider.WEBPAY]: {
    label: 'Webpay',
    logo: webpayLogo,
    adapter: (transaction: Transaction) => {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = transaction.url!;

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'token_ws';
      input.value = transaction.token!;

      form.appendChild(input);
      document.body.appendChild(form);
      form.submit();
    },
  },
};

const ProviderSelect: React.FC<Props> = ({
  open,
  onClose,
  amount,
  returnUrl,
  purpose,
  requestId,
}) => {
  const dispatch = useDispatch();
  const domain = window.location.hostname;
  const secretId = domain.split('.')[0];
  const { mutate, isPending } = createTransaction(dispatch, secretId);

  const formattedAmount = React.useMemo(
    () =>
      new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        maximumFractionDigits: 0,
      }).format(amount),
    [amount],
  );

  const handler = (provider: TransactionProvider) => {
    const adapter = providers[provider].adapter;
    mutate(
      {
        amount,
        provider,
        url: returnUrl,
        purpose,
        ...(requestId ? { requestId } : {}),
      },
      {
        onSuccess: (response) => {
          if (!response) {
            showAlert(dispatch, 'error', 'Problemas al generar el pago');
            return;
          }
          if (!response.token || !response.url) {
            showAlert(dispatch, 'error', 'Problemas al realizar el pago');
            return;
          }
          if (typeof document === 'undefined') {
            showAlert(dispatch, 'error', 'Problemas al redirigir al pago');
            return;
          }
          const originUrl = window.location.href;
          dispatch(saveTransaction({ transaction: response, originUrl }));
          adapter?.(response);
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <PageHeader.TitleOnly
          title="Pago"
          subtitle="Elige el medio de pago"
          iconType="payment"
        />
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box
            sx={{
              borderRadius: 2,
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: 'grey.200',
              px: 2,
              py: 1.5,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Monto a pagar
            </Typography>
            <Typography variant="h5" fontWeight={600}>
              {formattedAmount}
            </Typography>
          </Box>

          {Object.entries(providers).map(([key, provider]) => (
            <Button
              key={key}
              variant="outlined"
              fullWidth
              onClick={() => handler(key as TransactionProvider)}
              sx={{
                justifyContent: 'space-between',
                borderRadius: 2,
                borderColor: 'grey.300',
                textTransform: 'none',
                py: 1.5,
                px: 2,
                bgcolor: 'background.paper',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'background.paper',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  component="img"
                  src={provider.logo}
                  alt={provider.label}
                  sx={{ height: 28 }}
                />
              </Box>
              {isPending ? (
                <CircularProgress size={16} />
              ) : (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 500 }}
                >
                  {provider.label}
                </Typography>
              )}
            </Button>
          ))}

          <Typography
            variant="caption"
            color="text.secondary"
            align="center"
            sx={{ mt: 0.5 }}
          >
            Serás redirigido a una página de pago segura del proveedor
            seleccionado.
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ProviderSelect;
